import type { JSONContent } from '@tiptap/react';

import type { TypedTable } from './model';
import { displayCell } from './table';

export function blockText(node: JSONContent, markdown = false): string {
  if (node.type === 'text') {
    let text = node.text ?? '';

    if (markdown) {
      for (const mark of node.marks ?? []) {
        if (mark.type === 'bold') {
          text = `**${text}**`;
        }

        if (mark.type === 'italic') {
          text = `*${text}*`;
        }

        if (mark.type === 'code') {
          text = `\`${text}\``;
        }

        if (mark.type === 'link') {
          text = `[${text}](${mark.attrs?.href ?? ''})`;
        }
      }
    }

    return text;
  }

  if (node.type === 'hardBreak') {
    return '\n';
  }

  if (node.type === 'image') {
    return markdown
      ? `![${node.attrs?.alt ?? ''}](${node.attrs?.src ?? ''})`
      : String(node.attrs?.src ?? '');
  }

  if (node.type === 'typedTable') {
    const table = node.attrs?.table as TypedTable;

    if (!table?.columns) {
      return '';
    }

    const escapeCell = (s: string) =>
      markdown ? s.replaceAll('|', '\\|').replaceAll('\n', '<br>') : s.replaceAll('\t', ' ');

    const rows = [
      table.columns.map(c => c.name),
      ...table.rows.map(r => table.columns.map(c => displayCell(c, r.cells[c.id]))),
    ];

    if (!markdown) {
      return rows.map(row => row.join('\t')).join('\n');
    }

    rows.splice(
      1,
      0,
      table.columns.map(() => '---'),
    );

    return rows.map(row => `| ${row.map(escapeCell).join(' | ')} |`).join('\n');
  }

  const inline = (node.content ?? []).map(n => blockText(n, markdown));

  if (node.type === 'codeBlock') {
    const text = (node.content ?? []).map(n => n.text ?? '').join('');

    if (!markdown) {
      return text;
    }

    const fences = Math.max(3, ...(text.match(/`+/g) ?? []).map(s => s.length + 1));

    return `${'`'.repeat(fences) + (node.attrs?.language ?? '')}\n${text}\n${'`'.repeat(fences)}`;
  }

  if (node.type === 'heading') {
    return (markdown ? `${'#'.repeat(node.attrs?.level ?? 2)} ` : '') + inline.join('');
  }

  if (node.type === 'taskItem') {
    return (
      (markdown
        ? `- [${node.attrs?.checked ? 'x' : ' '}] `
        : `${node.attrs?.checked ? '☑' : '☐'} `) + inline.join('\n')
    );
  }

  if (node.type === 'listItem') {
    return (markdown ? '- ' : '• ') + inline.join('\n');
  }

  if (node.type === 'blockquote') {
    return markdown
      ? inline
          .join('\n')
          .split('\n')
          .map(l => `> ${l}`)
          .join('\n')
      : inline.join('\n');
  }

  if (['doc', 'bulletList', 'orderedList', 'taskList'].includes(node.type ?? '')) {
    return inline.join(node.type === 'doc' ? '\n\n' : '\n');
  }

  return inline.join('');
}
